/**
 * Inicio de Staff (006 · T026 · US1): puerta de entrada de la app instalada.
 *
 * La puerta (activación a medias, clave del dispositivo, sesión vencida, empresa suspendida,
 * aviso de privacidad pendiente) vive en `useAccessGate` (`features/shell/use-access-gate.ts`,
 * T017), compartida con Asistencia y Perfil; aquí solo se decide qué pintar según su resultado.
 * La home real —saludo, fecha, tarjeta "Tu jornada", últimas marcaciones y el botón de
 * marcación— vive en `features/home/home-screen.tsx` (T024–T026).
 */
import { createFileRoute } from '@tanstack/react-router';

import { Screen } from '../components/ui/screen';
import { SkeletonText } from '../components/ui/skeleton';
import { DEVICE_KEY_QUERY_KEY } from '../features/access/device-key-query';
import { PrivacyUpdateScreen } from '../features/access/privacy-update';
import { HomeScreen } from '../features/home/home-screen';
import { GateFallback } from '../features/shell/gate-fallback';
import { SESSION_EXPIRED_REASON, useAccessGate } from '../features/shell/use-access-gate';
import { t } from '../i18n';

/**
 * Se siguen exportando desde aquí (research R-3): `tests/unit/home.test.tsx` y otras pantallas los
 * importan de `routes/index`. La fuente es `features/shell/use-access-gate.ts`.
 */
export { DEVICE_KEY_QUERY_KEY, SESSION_EXPIRED_REASON };

function GateLoading() {
  return (
    <Screen data-testid="home-loading" align="center">
      <SkeletonText label={t('common.states.loading')} />
    </Screen>
  );
}

export function HomeGate() {
  const gate = useAccessGate({ allowSuspended: false });

  if (gate.status === 'LOADING') {
    return <GateLoading />;
  }
  if (gate.status === 'OFFLINE' || gate.status === 'ERROR') {
    return <GateFallback status={gate.status} retry={gate.retry} active="home" testId="home-placeholder" />;
  }
  if (gate.status === 'PRIVACY_PENDING') {
    return <PrivacyUpdateScreen version={gate.me.privacy_notice.version} />;
  }
  return <HomeScreen me={gate.me} />;
}

export const Route = createFileRoute('/')({
  component: HomeGate,
});
